import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
// import { formatDistanceToNow } from "date-fns"; // Kept for future use
import { toast } from "react-toastify";
import {
  Search,
  // Filter, // Kept for future use
  RefreshCw,
  Plus,
  ArrowUpDown,
  // Eye, // Kept for future use
  // Edit, // Kept for future use
  // CheckCircle, // Kept for future use
  // XCircle, // Kept for future use
  X,
  // AlertTriangle, // Kept for future use
  // Clock, // Kept for future use
  // Calendar, // Kept for future use
  Layers,
  // Settings, // Kept for future use
  FileText,
  // ChevronDown, // Kept for future use
  // ChevronRight, // Kept for future use
  Building2,
  // Wrench, // Kept for future use
  ListChecks,
  // BarChart3, // Kept for future use
  // TrendingUp, // Kept for future use
  // Activity, // Kept for future use
  // Zap, // Kept for future use
  // ExternalLink, // Kept for future use
  // ChevronUp, // Kept for future use
  // Users, // Kept for future use
  Target,
  CheckSquare,
  Box,
  Flag,
  Folder,
} from "lucide-react";
import {
  Checkpoint,
  AssetWithCheckpoints,
} from "../../../types/checkpoint";
import { Asset } from "../../../types/asset";
import { ProjectTypeStats, AssetStats } from "../../../types/checklist";
import Select from "../../../shared/components/form/Select";
import Button from "../../../shared/components/ui/button/Button";
import api from "../../../services/axiosInstance";
import { checkpointService } from "../../../services/checkpointService";
import { amsCheckpointService, AmsCheckpoint } from "../../../services/amsCheckpointService";
import projectTypeService, { ProjectType } from "../../../services/projectTypeService";
import AssetCheckpointsDrawer from "./AssetCheckpointsDrawer";
import { getDefaultBufferTime } from "../utils/checkpointUtils";
import AmsCheckpointGrid from "./ams/AmsCheckpointGrid";
import AmsCheckpointDrawer from "./ams/AmsCheckpointDrawer";

// API Constants - Maximum limits enforced by backend
const CHECKPOINT_API_MAX_LIMIT = 100; // Maximum limit per page for checkpoint API queries (backend validation: max 100)

// Modern Clean Brand Colors - Subtle & Refined (matching UserForm.tsx)
const BRAND_COLORS = {
  primary: "#339274",          // Primary Green
  primaryHover: "#29795f",     // Darker Green for hover
  primaryLight: "#F8FCFA",     // Very subtle green background
  primarySoft: "#EDF7F2",      // Soft green background
  accent: "#A3CD39",           // Lime accent
  dark: "#007F66",             // Deep teal
  success: "#10B981",
  successLight: "#F0FDF4",
  error: "#EF4444",
  errorLight: "#FEF2F2",
  warning: "#F59E0B",
  warningLight: "#FFFBEB",
};

// Frequency color mapping - using brand colors where possible (kept for future use)
// const FREQUENCY_COLORS = {
//   'Daily': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
//   'Weekly': { bg: 'bg-[#339274]/10', text: 'text-[#339274]', border: 'border-[#339274]/20', dot: 'bg-[#339274]' },
//   'Monthly': { bg: 'bg-[#A3CD39]/10', text: 'text-[#A3CD39]', border: 'border-[#A3CD39]/20', dot: 'bg-[#A3CD39]' },
//   'Quarterly': { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', dot: 'bg-purple-500' },
//   'Yearly': { bg: 'bg-[#F59E0B]/10', text: 'text-[#F59E0B]', border: 'border-[#F59E0B]/20', dot: 'bg-[#F59E0B]' },
// };

// Safely format relative time; returns null if invalid (kept for future use)
// const formatRelativeTime = (dateInput?: string | Date | null): string | null => {
//   if (!dateInput) return null;
//   const date = new Date(dateInput);
//   if (isNaN(date.getTime())) return null;
//   try {
//     return formatDistanceToNow(date, { addSuffix: true });
//   } catch {
//     return null;
//   }
// };



// Enhanced Project Type Selector Component
interface ProjectTypeSelectorProps {
  projectTypeStats: ProjectTypeStats[];
  selectedProjectType: string;
  onProjectTypeChange: (projectTypeId: string) => void;
  statusFilter: string;
  checkpointType?: 'OMS' | 'AMS';
  amsProjectCheckpoints?: Array<{ projectType: { _id: string; id?: string; name: string; code?: string }; count: number }>;
  onAmsProjectView?: (projectType: { _id: string; id?: string; name: string; code?: string }) => void;
}

const ProjectTypeSelector: React.FC<ProjectTypeSelectorProps> = ({
  projectTypeStats,
  selectedProjectType,
  onProjectTypeChange,
  statusFilter,
  checkpointType,
  amsProjectCheckpoints = [],
  onAmsProjectView,
}) => {
  // Calculate display stats for each project type
  const getProjectTypeDisplayStats = (stats: ProjectTypeStats) => {
    return {
      assets: stats.totalAssets,
      checkpoints: statusFilter === 'All'
        ? stats.totalCheckpoints
        : statusFilter === 'active'
          ? stats.activeCheckpoints
          : stats.inactiveCheckpoints,
      // AMS-only breakdown — undefined for OMS, callers should fall back to the combined `checkpoints` count.
      assetCheckpoints: stats.assetCheckpoints,
      projectCheckpoints: stats.projectCheckpoints,
      hasContent: stats.totalAssets > 0 && stats.totalCheckpoints > 0,
      isEmpty: stats.totalAssets === 0 && stats.totalCheckpoints === 0
    };
  };

  // Get AMS project checkpoint count for a specific project type
  const getAmsProjectCheckpointCount = (projectTypeId: string) => {
    if (checkpointType !== 'AMS') return 0;
    const projectCheckpoint = amsProjectCheckpoints.find(
      item => (item.projectType._id || item.projectType.id) === projectTypeId
    );
    return projectCheckpoint ? projectCheckpoint.count : 0;
  };

  return (
    <div className="bg-white border-b border-gray-200 px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Building2 size={18} className="text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Project Type:</span>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {projectTypeStats.map((projectTypeStat) => {
          const isSelected = selectedProjectType === (projectTypeStat.id || projectTypeStat._id);
          const stats = getProjectTypeDisplayStats(projectTypeStat);
          const amsProjectCount = checkpointType === 'AMS' ? getAmsProjectCheckpointCount(projectTypeStat.id || projectTypeStat._id) : null;

          let pillStyling = "bg-gray-50/50 border border-gray-200";
          let textColor = "text-slate-600";
          let badgeColor = "bg-slate-100 text-slate-500 border-slate-200";

          if (isSelected) {
            pillStyling = "bg-gradient-to-r from-[#339274]/12 to-[#a3cd39]/18 border-[#339274] border-2";
            textColor = "text-[#339274] font-semibold";
            badgeColor = "bg-[#a3cd39]/20 text-[#339274] border-[#a3cd39]/30";
          } else if (stats.isEmpty) {
            pillStyling = "bg-gray-50/50 border border-gray-200 hover:bg-gray-100/70";
            textColor = "text-slate-500";
          } else if (stats.hasContent) {
            pillStyling = "bg-emerald-50/60 border border-emerald-200 hover:bg-emerald-100/80";
            textColor = "text-emerald-700";
            badgeColor = "bg-emerald-100 text-emerald-600 border-emerald-300";
          } else {
            pillStyling = "bg-amber-50/60 border border-amber-200 hover:bg-amber-100/80";
            textColor = "text-amber-700";
            badgeColor = "bg-amber-100 text-amber-600 border-amber-300";
          }

          return (
            <div key={projectTypeStat._id} className="flex flex-col gap-2">
              <div className={`group rounded-lg transition-all duration-200 ${pillStyling} ${isSelected
                  ? "ring-1 ring-[#339274]/50 shadow-sm scale-[1.01]"
                  : "hover:shadow-xs"
                }`}>
                {/* Tab Header */}
                <button
                  onClick={() => onProjectTypeChange(projectTypeStat.id || projectTypeStat._id || "")}
                  className="w-full inline-flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg border-0 bg-transparent hover:bg-transparent focus:outline-none focus:ring-0"
              >
                {/* Left: Check + Name */}
                <div className="flex items-center gap-1.5">
                  {isSelected && <CheckSquare size={12} className="text-[#339274]" />}
                  <span className={`text-xs font-medium truncate ${textColor}`}>
                    {projectTypeStat.name}
                  </span>
                </div>

                {/* Right: Stats */}
                <div className="flex items-center gap-1">
                  <span
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-full border ${badgeColor}`}
                    title={`${stats.assets} asset${stats.assets === 1 ? '' : 's'} with checkpoints`}
                  >
                    <Box size={10} className="opacity-70" />
                    {stats.assets}A
                  </span>
                  {checkpointType === 'AMS' ? (
                    <>
                      <span
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-full border ${badgeColor}`}
                        title={`${stats.assetCheckpoints ?? 0} asset-level checkpoint${(stats.assetCheckpoints ?? 0) === 1 ? '' : 's'}`}
                      >
                        <Flag size={10} className="opacity-70" />
                        {stats.assetCheckpoints ?? 0}AC
                      </span>
                      <span
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-full border ${badgeColor}`}
                        title={`${stats.projectCheckpoints ?? 0} project-level checkpoint${(stats.projectCheckpoints ?? 0) === 1 ? '' : 's'}`}
                      >
                        <Layers size={10} className="opacity-70" />
                        {stats.projectCheckpoints ?? 0}PC
                      </span>
                    </>
                  ) : (
                    <span
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-full border ${badgeColor}`}
                      title={`${stats.checkpoints} total checkpoint${stats.checkpoints === 1 ? '' : 's'}`}
                    >
                      <Flag size={10} className="opacity-70" />
                      {stats.checkpoints}C
                    </span>
                  )}
                </div>
                </button>

                {/* Project-level checkpoints now live in the content-area toggle (Project-level
                    Checkpoints | Assets), so the per-tab strip was removed to avoid duplication. */}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Toggle Status Cell Component
// interface ToggleStatusCellProps { // Kept for future use
//   checkpoint: Checkpoint;
//   onRefresh: () => void;
// }

// const ToggleStatusCell: React.FC<ToggleStatusCellProps> = ({ checkpoint, onRefresh }) => {
//   const [isToggling, setIsToggling] = useState(false);

//   const handleToggle = async () => {
//     if (isToggling) return;

//     setIsToggling(true);
//     try {
//       await checkpointService.toggleCheckpointStatus(checkpoint.id || checkpoint._id);
//       toast.success(`Checkpoint ${checkpoint.status === 'active' ? 'deactivated' : 'activated'} successfully`);
//       onRefresh();
//     } catch (error) {
//       console.error('Failed to toggle checkpoint status:', error);
//       toast.error('Failed to update checkpoint status');
//     } finally {
//       setIsToggling(false);
//     }
//   };

//   return (
//     <Button
//       onClick={handleToggle}
//       disabled={isToggling}
//       variant="outline"
//       size="sm"
//       className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-200 ${checkpoint.status === 'active'
//           ? 'bg-green-100 text-green-700 border border-green-200 hover:bg-green-200'
//           : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
//         } ${isToggling ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
//     >
//       {isToggling ? (
//         <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin mr-1" />
//       ) : (
//         <div className={`w-2 h-2 rounded-full mr-1 ${checkpoint.status === 'active' ? 'bg-green-500' : 'bg-gray-400'
//           }`} />
//       )}
//       {checkpoint.status === 'active' ? 'active' : 'Inactive'}
//     </Button>
//   );
// };



// Asset Card Component
interface AssetCardProps {
  assetData: AssetWithCheckpoints;
  onAdd: (assetData: AssetWithCheckpoints, checkpointData?: { name: string; frequency: string; bufferTimeDays?: number; requirement?: string }) => void;
  onView: (assetData: AssetWithCheckpoints) => void;
}

const AssetCard: React.FC<AssetCardProps> = ({ assetData, onAdd, onView }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [checkpointName, setCheckpointName] = useState('');
  const [frequency, setFrequency] = useState<'Daily' | 'Weekly' | 'Fortnightly' | 'Monthly' | 'Quarterly' | 'Half-Yearly' | 'Yearly'>('Weekly');
  const [bufferTimeDays, setBufferTimeDays] = useState<number>(1); // Grace period in days
  const [requirement, setRequirement] = useState(''); // Optional requirement field
  const [isSubmitting, setIsSubmitting] = useState(false);

  const projectTypeName =
    typeof assetData.asset.projectTypeId === "object" && assetData.asset.projectTypeId
      ? assetData.asset.projectTypeId.name
      : "Unknown";

  const handleAddClick = () => {
    setShowAddForm(!showAddForm);
    if (!showAddForm) {
      setCheckpointName('');
      setFrequency('Weekly');
      setBufferTimeDays(getDefaultBufferTime('Weekly'));
      setRequirement(''); // Reset requirement field
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!checkpointName.trim()) return;

    setIsSubmitting(true);
    try {
      // Call the onAdd function with the new checkpoint data
      await onAdd(assetData, { name: checkpointName.trim(), frequency, bufferTimeDays, requirement: requirement.trim() });

      // Reset form and close
      setCheckpointName('');
      setFrequency('Weekly');
      setBufferTimeDays(getDefaultBufferTime('Weekly'));
      setRequirement(''); // Reset requirement field
      setShowAddForm(false);
    } catch (error) {
      console.error('Failed to add checkpoint:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setCheckpointName('');
    setFrequency('Weekly');
    setBufferTimeDays(getDefaultBufferTime('Weekly'));
    setRequirement(''); // Reset requirement field
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2, scale: 1.02 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="relative bg-white rounded-2xl shadow-sm hover:shadow-md border border-gray-100 p-5 transition-all duration-200 group flex flex-col gap-4"
    >
      {/* Project Type Tag */}
      <div className="absolute right-0 top-0 m-0.5">
        <span className="text-[7px] font-normal text-[#339274] tracking-wide px-1 py-0.5 rounded-sm" style={{ lineHeight: '1', letterSpacing: '0.04em' }}>
          {projectTypeName}
        </span>
      </div>

      {/* Asset Header - Better for Long Names */}
      <div className="flex items-start gap-3 pt-1">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#339274]/15 to-[#339274]/10 border border-[#339274]/20 flex-shrink-0">
          <Building2 size={20} className="text-[#339274]" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-800 group-hover:text-[#339274] transition-colors duration-200 leading-tight break-words">
            {assetData.asset.name}
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">Asset</p>
        </div>
      </div>

      {/* Checkpoints Section - Compact */}
      <div className="flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-gray-50 to-gray-100/50 border border-gray-200/50 hover:from-gray-100 hover:to-gray-200/70 hover:border-[#339274]/30 transition-all duration-200">
        {/* Checkpoints Info - Clickable */}
        <div
          onClick={() => onView(assetData)}
          className="flex items-center gap-2.5 cursor-pointer group/checkpoint flex-1"
        >
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#339274]/10 border border-[#339274]/20">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              className="text-[#339274]"
            >
              <path
                d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div>
            <span className="text-xs font-medium text-gray-600 group-hover/checkpoint:text-[#339274] transition-colors">
              {assetData.totalCheckpoints} Checkpoints
            </span>
            <p className="text-[10px] text-gray-400 mt-0.5">View & manage</p>
          </div>
          <div className="text-[#339274] opacity-60 group-hover/checkpoint:opacity-100 transition-opacity ml-auto">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>

      </div>
    </motion.div>
  );
};

// Assets Grid Component
interface AssetsGridProps {
  assetsWithCheckpoints: AssetWithCheckpoints[];
  search: string;
  setSearch: (val: string) => void;
  onAssetCheckpointsClick: (assetData: AssetWithCheckpoints) => void;
  onAdd: (assetData: AssetWithCheckpoints, checkpointData?: { name: string; frequency: string; bufferTimeDays?: number }) => void;
  onBulkAdd: () => void;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  isLoading?: boolean;
  currentPage: number;
  hasMore: boolean;
  onLoadMore: () => void;
}

const AssetsGrid: React.FC<AssetsGridProps> = ({
  assetsWithCheckpoints,
  search,
  setSearch,
  onAssetCheckpointsClick,
  onAdd,
  onBulkAdd,
  statusFilter,
  onStatusFilterChange,
  isLoading = false,
  // currentPage, // Kept for future use
  hasMore,
  onLoadMore,
}) => {
  const navigate = useNavigate();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [sortMenuOpen, setSortMenuOpen] = useState<string | null>(null);
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");


  useEffect(() => {
    const handleShortcut = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  useEffect(() => {
    const handleKeyboard = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && searchInputRef.current === document.activeElement) {
        setSearch('');
      }
    };

    document.addEventListener('keydown', handleKeyboard);
    return () => document.removeEventListener('keydown', handleKeyboard);
  }, [setSearch]);

  const handleSortOption = (field: string, order: "asc" | "desc") => {
    setSortField(field);
    setSortOrder(order);
    setSortMenuOpen(null);
  };

  // Filter assets by search term (asset name)
  const filteredAssets = assetsWithCheckpoints.filter(assetData => {
    if (!search.trim()) return true;
    return assetData.asset.name.toLowerCase().includes(search.toLowerCase());
  });

  // Sort filtered assets
  const sortedAssets = [...filteredAssets].sort((a, b) => {
    if (sortField === "name") {
      const nameA = a.asset.name.toLowerCase();
      const nameB = b.asset.name.toLowerCase();
      return sortOrder === "asc" ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    }
    return 0;
  });

  return (
    <div className="space-y-6">
      {/* Sticky Filter Bar */}
      <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-white to-gray-25">
        {/* Main Controls Row */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Left Side: Search and Filter */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            {/* Search Bar */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={16} className="text-gray-400" />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by asset name... (Ctrl+K)"
                className="block w-full sm:w-80 pl-9 pr-4 py-2.5 text-sm font-medium border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all duration-200 bg-white"
                style={{
                  '--tw-ring-color': BRAND_COLORS.primary,
                  'focusBorderColor': BRAND_COLORS.primary
                } as React.CSSProperties}
              />
            </div>

            {/* Filter Button */}
            {/* <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-2 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 border ${showFilters
                  ? `text-white border-transparent`
                  : "text-gray-600 bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                }`}
              style={showFilters ? {
                background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`
              } : {}}
            >
              <div className="w-4 h-4">🔍</div>
              Filters
            </button> */}
          </div>

          {/* Right Side: Refresh, Sort, and Add Buttons */}
          <div className="flex items-center gap-3">
            {/* Control Buttons */}

            {/* Bulk Checklist Buttons */}
            <div className="flex items-center gap-3">
              <button
                onClick={onBulkAdd}
                className="inline-flex items-center gap-3 px-5 py-2.5 text-sm font-medium text-white border border-transparent rounded-xl shadow-lg shadow-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-blue-200"
                style={{
                  background: `linear-gradient(135deg, #3B82F6, #1D4ED8)`,
                  '--tw-ring-color': '#3B82F6'
                } as React.CSSProperties}
              >
                <div className="relative">
                  <ListChecks size={16} className="absolute animate-ping opacity-40" />
                  <ListChecks size={16} className="relative" />
                </div>
                <span>Create Checkpoints</span>
              </button>

              {/* Upload Bulk Excel Button */}
              <button
                onClick={() => navigate('/masters/checkpoints/bulk-upload')}
                className="inline-flex items-center gap-3 px-5 py-2.5 text-sm font-medium text-white border border-transparent rounded-xl shadow-lg shadow-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-green-200"
                style={{
                  background: `linear-gradient(135deg, #10B981, #059669)`,
                  '--tw-ring-color': '#10B981'
                } as React.CSSProperties}
              >
                <div className="relative">
                  <FileText size={16} className="absolute animate-ping opacity-40" />
                  <FileText size={16} className="relative" />
                </div>
                <span>Upload Excel</span>
              </button>
            </div>




          </div>
        </div>

        {/* Filters */}
        {/* Filters removed as per instructions */}
      </div>

      {/* Assets Grid */}
      <div className="px-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <div className="flex flex-col items-center gap-2">
              <div className="w-7 h-7 border-2 border-blue-100 border-t-[#339274] rounded-full animate-spin" />
              <span className="text-sm font-medium text-gray-700">Loading assets...</span>
            </div>
          </div>
        ) : sortedAssets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center bg-gradient-to-b from-white via-blue-50 to-white rounded-xl shadow-inner">
            <Target size={36} className="text-blue-100 mb-2" />
            <h3 className="text-base font-semibold text-gray-800 mb-1">
              {search.trim() ? 'No assets found matching your search' : 'No assets found'}
            </h3>
            <p className="text-xs text-gray-500">
              {search.trim() ? 'Try adjusting your search terms' : 'Try adjusting your filters'}
            </p>
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => setSearch("")}
                className="px-3 py-1.5 rounded bg-gradient-to-r from-blue-400 to-blue-600 text-white text-xs font-medium shadow hover:from-blue-500 hover:to-blue-700 transition"
              >
                Clear Search
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Search Results Info */}
            {search.trim() && (
              <div className="mb-4 p-2 bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-100 rounded-lg shadow-sm">
                <div className="flex items-center gap-2 text-blue-900">
                  <Search size={14} className="opacity-80" />
                  <span className="text-xs font-medium">
                    Showing <span className="text-blue-700">{sortedAssets.length}</span> asset{sortedAssets.length !== 1 ? 's' : ''} matching <span className="font-mono bg-blue-200/60 px-1 py-0.5 rounded text-blue-900">"{search}"</span>
                  </span>
                </div>
              </div>
            )}

            {/* Grid Layout - 4 Cards Per Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {sortedAssets.map((assetData) => (
                <div key={assetData.asset.id || assetData.asset._id} className="transition-transform hover:scale-[1.01]">
                  <AssetCard
                    assetData={assetData}
                    onAdd={onAdd}
                    onView={onAssetCheckpointsClick}
                  />
                </div>
              ))}
            </div>

            {/* Load More Button */}
            {hasMore && (
              <div className="flex justify-center pt-6">
                <Button
                  onClick={onLoadMore}
                  variant="outline"
                  size="sm"
                  className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-xl border hover:shadow-lg hover:scale-105 transition-all duration-200 shadow border-[#339274] text-[#339274] hover:bg-[#339274] hover:text-white"
                >
                  <RefreshCw size={16} className="animate-spin-slow" />
                  Load More
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Main CheckpointTable Component
interface CheckpointTableProps {
  checkpoints: Checkpoint[];
  search: string;
  setSearch: (val: string) => void;
  projectType: string;
  setProjectType: (val: string) => void;
  asset: string;
  setAsset: (val: string) => void;
  frequency: string;
  setFrequency: (val: string) => void;
  status: string | null;
  setStatus: (val: string | null) => void;
  page: number;
  setPage: (val: number) => void;
  totalCheckpoints: number;
  projectTypes: any[]; // Using any[] to avoid type conflicts between different ProjectType interfaces
  assets: Asset[];

  onView: (checkpoint: Checkpoint) => void;
  onAdd: (assetData?: AssetWithCheckpoints) => void; // Modified to accept optional asset data
  onBulkAdd: () => void;
  onRefresh: () => void;
  sortField: string;
  sortOrder: "asc" | "desc";
  setSortField: (field: string) => void;
  setSortOrder: (order: "asc" | "desc") => void;
}

const CheckpointTable: React.FC<CheckpointTableProps> = ({
  // checkpoints, // Kept for future use
  search,
  setSearch,
  projectType,
  setProjectType,
  // asset, // Kept for future use
  // setAsset, // Kept for future use
  // frequency, // Kept for future use
  // setFrequency, // Kept for future use
  // status, // Kept for future use
  // setStatus, // Kept for future use
  // page, // Kept for future use
  setPage,
  // totalCheckpoints, // Kept for future use
  // projectTypes, // Kept for future use
  assets,

  // onView, // Kept for future use
  onAdd,
  onBulkAdd,
  onRefresh: _onRefresh,
  // sortField, // Kept for future use
  // sortOrder, // Kept for future use
  // setSortField, // Kept for future use
  // setSortOrder, // Kept for future use
}) => {
  // ========== State Management ==========
  const [viewMode] = useState<'table' | 'assets'>('assets');
  // Use global activeDepartment from auth context instead of local tab
  const { activeDepartment } = useAuth();
  const checkpointType = activeDepartment === 'AMS' ? 'AMS' : 'OMS';
  const [projectTypeStats, setProjectTypeStats] = useState<ProjectTypeStats[]>([]);
  const [assetsWithCheckpoints, setAssetsWithCheckpoints] = useState<AssetWithCheckpoints[]>([]);
  const [selectedAssetData, setSelectedAssetData] = useState<AssetWithCheckpoints | null>(null);
  const [showAssetCheckpointsDrawer, setShowAssetCheckpointsDrawer] = useState(false);
  const [statusFilter, setStatusFilter] = useState('All');
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  
  // AMS specific state
  const [amsCheckpointTypeFilter, setAmsCheckpointTypeFilter] = useState<'asset' | 'project' | 'all'>('all');
  const [selectedAmsAsset, setSelectedAmsAsset] = useState<Asset | null>(null);
  const [selectedAmsProject, setSelectedAmsProject] = useState<{ _id: string; id?: string; name: string; code?: string } | null>(null);
  const [showAmsDrawer, setShowAmsDrawer] = useState(false);
  const [amsProjectCheckpoints, setAmsProjectCheckpoints] = useState<Array<{ projectType: { _id: string; id?: string; name: string; code?: string }; count: number }>>([]);
  const [isLoadingAmsProjects, setIsLoadingAmsProjects] = useState(false);

  // Pagination state for assets grid
  const [assetsPage, setAssetsPage] = useState(1);
  const [assetsPerPage] = useState(12);
  const [hasMoreAssets, setHasMoreAssets] = useState(true);

  // ========== Data Fetching ==========

  const fetchProjectTypeStats = async () => {
    try {
      setIsLoadingStats(true);
      const response = await api.get('/checkpoints/stats/project-types', {
        params: { status: statusFilter }
      });
      setProjectTypeStats(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch project type stats:', error);
    } finally {
      setIsLoadingStats(false);
    }
  };

  const fetchAmsProjectTypeStats = async () => {
    try {
      setIsLoadingStats(true);
      // Fetch all AMS checkpoints to calculate stats
      const filters: any = {};
      if (statusFilter !== 'All') {
        filters.status = statusFilter.toLowerCase();
      }
      
      const response = await amsCheckpointService.getAmsCheckpoints(filters);
      const checkpoints: AmsCheckpoint[] = response.data || [];

      // Fetch project types to get names (in case they're not populated in checkpoints)
      let projectTypesMap = new Map<string, string>();
      try {
        const projectTypesResponse = await projectTypeService.getAllProjectTypes();
        const projectTypes: ProjectType[] = Array.isArray(projectTypesResponse) 
          ? projectTypesResponse 
          : (projectTypesResponse as any)?.data || [];
        projectTypes.forEach((pt: ProjectType) => {
          const id = pt._id || pt.id;
          if (id) {
            projectTypesMap.set(id, pt.name);
          }
        });
      } catch (error) {
        console.warn('Failed to fetch project types for AMS stats:', error);
      }

      // Group by projectTypeId and calculate stats
      const statsMap = new Map<string, {
        _id: string;
        id: string;
        name: string;
        totalCheckpoints: number;
        activeCheckpoints: number;
        inactiveCheckpoints: number;
        totalAssets: number;
        assetIds: Set<string>;
        assetCheckpoints: number;
        projectCheckpoints: number;
      }>();

      checkpoints.forEach((checkpoint) => {
        const projectTypeId = typeof checkpoint.projectTypeId === 'string' 
          ? checkpoint.projectTypeId 
          : checkpoint.projectTypeId._id || checkpoint.projectTypeId.id;
        
        if (!projectTypeId) return;

        // Get project type name from populated object or from map
        let projectTypeName = 'Unknown';
        if (typeof checkpoint.projectTypeId === 'object' && checkpoint.projectTypeId && 'name' in checkpoint.projectTypeId) {
          projectTypeName = checkpoint.projectTypeId.name;
        } else if (projectTypesMap.has(projectTypeId)) {
          projectTypeName = projectTypesMap.get(projectTypeId) || 'Unknown';
        }

        let stat = statsMap.get(projectTypeId);
        if (!stat) {
          stat = {
            _id: projectTypeId,
            id: projectTypeId,
            name: projectTypeName,
            totalCheckpoints: 0,
            activeCheckpoints: 0,
            inactiveCheckpoints: 0,
            totalAssets: 0,
            assetIds: new Set<string>(),
            assetCheckpoints: 0,
            projectCheckpoints: 0,
          };
          statsMap.set(projectTypeId, stat);
        }

        // Count checkpoints
        stat.totalCheckpoints++;
        if (checkpoint.status === 'active') {
          stat.activeCheckpoints++;
        } else {
          stat.inactiveCheckpoints++;
        }

        // Split by checkpointType so the UI can show "N asset-level" vs
        // "N project-level" instead of one ambiguous combined total.
        if (checkpoint.checkpointType === 'asset') {
          stat.assetCheckpoints++;
        } else if (checkpoint.checkpointType === 'project') {
          stat.projectCheckpoints++;
        }

        // Count unique assets (only for asset-type checkpoints)
        if (checkpoint.checkpointType === 'asset' && checkpoint.assetId) {
          const assetId = typeof checkpoint.assetId === 'string'
            ? checkpoint.assetId
            : checkpoint.assetId._id || checkpoint.assetId.id;
          if (assetId) {
            stat.assetIds.add(assetId);
          }
        }
      });

      // Convert map to array and set totalAssets
      let statsArray = Array.from(statsMap.values()).map(stat => ({
        ...stat,
        totalAssets: stat.assetIds.size,
      }));

      // If no checkpoints exist, create stats for all active project types with zero counts
      if (statsArray.length === 0 && projectTypesMap.size > 0) {
        statsArray = Array.from(projectTypesMap.entries()).map(([id, name]) => ({
          _id: id,
          id: id,
          name: name,
          totalCheckpoints: 0,
          activeCheckpoints: 0,
          inactiveCheckpoints: 0,
          totalAssets: 0,
          assetIds: new Set<string>(),
          assetCheckpoints: 0,
          projectCheckpoints: 0,
        }));
      }

      setProjectTypeStats(statsArray);
    } catch (error) {
      console.error('Failed to fetch AMS project type stats:', error);
      setProjectTypeStats([]);
    } finally {
      setIsLoadingStats(false);
    }
  };

  // Fetch AMS project checkpoints for ALL project types
  const fetchAmsProjectCheckpoints = async () => {
    if (checkpointType !== 'AMS') {
      setAmsProjectCheckpoints([]);
      return;
    }

    try {
      setIsLoadingAmsProjects(true);
      const filters: any = {
        'checkpoint-type': 'project',
        // Don't filter by project type - get all
      };

      const response = await amsCheckpointService.getAmsCheckpoints(filters);
      const checkpoints: AmsCheckpoint[] = response.data || [];

      // Group by projectTypeId
      const projectMap = new Map<string, { projectType: { _id: string; id?: string; name: string; code?: string }; count: number }>();

      checkpoints.forEach((checkpoint) => {
        if (checkpoint.checkpointType === 'project' && checkpoint.projectTypeId) {
          const projectId = typeof checkpoint.projectTypeId === 'string' 
            ? checkpoint.projectTypeId 
            : checkpoint.projectTypeId._id || checkpoint.projectTypeId.id;
          
          if (projectId) {
            const existing = projectMap.get(projectId);
            if (existing) {
              existing.count++;
            } else {
              const projectObj = typeof checkpoint.projectTypeId === 'object' 
                ? checkpoint.projectTypeId 
                : { _id: projectId, name: 'Unknown Project' };
              
              projectMap.set(projectId, {
                projectType: {
                  _id: projectId,
                  id: projectId,
                  name: typeof projectObj === 'object' && 'name' in projectObj ? projectObj.name : 'Unknown Project',
                  code: typeof projectObj === 'object' && 'code' in projectObj ? projectObj.code : undefined,
                },
                count: 1,
              });
            }
          }
        }
      });

      setAmsProjectCheckpoints(Array.from(projectMap.values()));
    } catch (error) {
      console.error('Failed to fetch AMS project checkpoints:', error);
      setAmsProjectCheckpoints([]);
    } finally {
      setIsLoadingAmsProjects(false);
    }
  };

  const fetchAssetsWithCheckpoints = async (page = 1, append = false) => {
    try {
      const response = await api.get('/checkpoints/stats/assets', {
        params: {
          status: statusFilter,
          projectTypeId: projectType || undefined,
          page,
          limit: assetsPerPage
        }
      });

      const assetsData = response.data.data || [];
      // const totalAssets = response.data.meta?.total || 0; // Kept for future use

      // Debug: Log the API response
      console.log('API Response for assets:', response.data);
      console.log('Assets data:', assetsData);

      const assetsWithCheckpointsData: AssetWithCheckpoints[] = assetsData.map((assetStat: AssetStats) => {
        // Try to find existing asset first
        const existingAsset = assets.find(a => (a.id || a._id) === assetStat.assetId);

        // Create asset object with proper project type handling
        const asset: Asset = existingAsset || {
          _id: assetStat.assetId,
          id: assetStat.assetId,
          name: assetStat.name,
          code: '', // Default or placeholder
          projectTypeId: {
            _id: typeof assetStat.projectTypeId === 'string' ? assetStat.projectTypeId : (assetStat.projectTypeId as any)._id,
            name: assetStat.projectTypeName || 'Unknown',
            status: 'active',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          status: 'active',
          assetStatus: 'active',
          specification: {},
          createdBy: { id: '', name: 'System', email: '' },
          updatedBy: { id: '', name: 'System', email: '' },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        // Use projectTypeName from the API response directly
        if (assetStat.projectTypeName) {
          asset.projectTypeId = {
            _id: typeof assetStat.projectTypeId === 'string' ? assetStat.projectTypeId : (assetStat.projectTypeId as any)._id,
            name: assetStat.projectTypeName,
            status: 'active',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
        }

        return {
          asset: asset as any as Asset,
          checkpoints: [],
          totalCheckpoints: assetStat.totalCheckpoints,
          activeCheckpoints: assetStat.activeCheckpoints,
        };
      });

      if (append) {
        setAssetsWithCheckpoints(prev => [...prev, ...assetsWithCheckpointsData]);
      } else {
        setAssetsWithCheckpoints(assetsWithCheckpointsData);
      }

      // Update pagination state
      setHasMoreAssets(assetsWithCheckpointsData.length === assetsPerPage);
      setAssetsPage(page);
    } catch (error) {
      console.error('Failed to fetch assets with checkpoints:', error);
    }
  };

  // ========== Effects ==========

  // Clear projectType immediately when switching to AMS
  useEffect(() => {
    if (checkpointType === 'AMS' && projectType) {
      setProjectType('');
    }
  }, [checkpointType]);

  useEffect(() => {
    if (viewMode === 'assets') {
      if (checkpointType === 'OMS') {
        fetchProjectTypeStats();
        fetchAssetsWithCheckpoints(1, false);
      } else if (checkpointType === 'AMS') {
        // Fetch AMS-specific project type stats
        fetchAmsProjectTypeStats();
        // Fetch AMS project checkpoints for ALL project types
        fetchAmsProjectCheckpoints();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, statusFilter, checkpointType, activeDepartment, projectType]);

  // Track previous projectTypeStats length to detect when it changes
  const prevProjectTypeStatsLengthRef = useRef<number>(0);
  const prevCheckpointTypeRef = useRef<'OMS' | 'AMS'>(checkpointType);

  // Default to the first Project Type tab so the page doesn't land on the
  // "Please select a Project Type" placeholder. Only overrides the selection
  // when it's missing/invalid (e.g. on load, or after switching to AMS) —
  // a manual pick of a later tab is left alone as long as it still exists
  // in the current stats list.
  useEffect(() => {
    const currentLength = projectTypeStats.length;
    const prevCheckpointType = prevCheckpointTypeRef.current;
    const justSwitchedToAMS = checkpointType === 'AMS' && prevCheckpointType !== 'AMS';

    if (currentLength > 0) {
      const firstProjectTypeId = projectTypeStats[0].id || projectTypeStats[0]._id;
      const currentSelectionStillValid = !!projectType && projectTypeStats.some(
        (pt) => (pt.id || pt._id) === projectType
      );
      if (firstProjectTypeId && (justSwitchedToAMS || !currentSelectionStillValid)) {
        setProjectType(firstProjectTypeId);
      }
    } else if (currentLength === 0 && projectType) {
      setProjectType('');
    }

    // Update the refs for next comparison
    prevProjectTypeStatsLengthRef.current = currentLength;
    prevCheckpointTypeRef.current = checkpointType;
  }, [projectTypeStats, projectType, setProjectType, checkpointType]);

  // ========== Event Handlers ==========

  const handleProjectTypeChange = (projectTypeId: string) => {
    setProjectType(projectTypeId);
    setPage(1); // Reset to first page
  };

  const handleAssetCheckpointsClick = async (assetData: AssetWithCheckpoints) => {
    try {
      console.log('Asset data received in handleAssetCheckpointsClick:', assetData);
      console.log('Asset ID:', assetData.asset.id || assetData.asset._id);

      // Fetch all checkpoints for this asset using pagination (max limit is 100 per API)
      let allCheckpoints: any[] = [];
      let currentPage = 1;
      let hasMore = true;

      // Fetch all pages of checkpoints
      while (hasMore) {
        const response = await checkpointService.getCheckpoints({
          assetId: assetData.asset.id || assetData.asset._id,
          limit: CHECKPOINT_API_MAX_LIMIT,
          page: currentPage
        });

        // Parse the response correctly - handle different possible response structures
        let pageCheckpoints: any[] = [];
        if (response && typeof response === 'object') {
          if ('data' in response && Array.isArray(response.data)) {
            // Direct array in data property
            pageCheckpoints = response.data;
          } else if ('data' in response && response.data && typeof response.data === 'object') {
            if ('checkpoints' in response.data && Array.isArray(response.data.checkpoints)) {
              // Nested checkpoints array
              pageCheckpoints = response.data.checkpoints;
            } else if (Array.isArray(response.data)) {
              // Data is an array
              pageCheckpoints = response.data;
            }
          } else if (Array.isArray(response)) {
            // Response is directly an array
            pageCheckpoints = response;
          }
        }

        allCheckpoints = [...allCheckpoints, ...pageCheckpoints];

        // Check if there are more pages
        const pagination = (response as any)?.pagination;
        if (pagination) {
          hasMore = currentPage < pagination.totalPages;
        } else {
          // If no pagination info, assume no more pages if we got less than the limit
          hasMore = pageCheckpoints.length === CHECKPOINT_API_MAX_LIMIT;
        }

        currentPage++;
      }

      console.log('Fetched checkpoints for asset:', allCheckpoints.length);

      // Calculate counts
      const totalCheckpoints = allCheckpoints.length;
      const activeCheckpoints = allCheckpoints.filter((cp: any) => cp.status === 'active').length;

      // Always show all checkpoints (active + inactive) in the drawer so users
      // can enable/disable them from here. Status filter only affects the outer grid stats.
      setSelectedAssetData({
        ...assetData,
        checkpoints: allCheckpoints,
        totalCheckpoints,
        activeCheckpoints
      });
      setShowAssetCheckpointsDrawer(true);
    } catch (error) {
      console.error('Failed to fetch checkpoints for asset:', error);
      toast.error('Failed to load checkpoints for this asset');
    }
  };

  const handleCloseAssetCheckpointsDrawer = () => {
    setShowAssetCheckpointsDrawer(false);
    setSelectedAssetData(null);
  };

  const handleRefreshAssetData = async () => {
    if (selectedAssetData) {
      try {
        // Fetch all checkpoints for this asset using pagination (max limit is 100 per API)
        let allCheckpoints: any[] = [];
        let currentPage = 1;
        let hasMore = true;

        // Fetch all pages of checkpoints
        while (hasMore) {
          const allCheckpointsResponse = await checkpointService.getCheckpoints({
            assetId: selectedAssetData.asset.id || selectedAssetData.asset._id,
            limit: CHECKPOINT_API_MAX_LIMIT,
            page: currentPage
          });

          // Parse the response correctly - handle different possible response structures
          let pageCheckpoints: any[] = [];
          if (allCheckpointsResponse && typeof allCheckpointsResponse === 'object') {
            if ('data' in allCheckpointsResponse && Array.isArray(allCheckpointsResponse.data)) {
              // Direct array in data property
              pageCheckpoints = allCheckpointsResponse.data;
            } else if ('data' in allCheckpointsResponse && allCheckpointsResponse.data && typeof allCheckpointsResponse.data === 'object') {
              if ('checkpoints' in allCheckpointsResponse.data && Array.isArray(allCheckpointsResponse.data.checkpoints)) {
                // Nested checkpoints array
                pageCheckpoints = allCheckpointsResponse.data.checkpoints;
              } else if (Array.isArray(allCheckpointsResponse.data)) {
                // Data is an array
                pageCheckpoints = allCheckpointsResponse.data;
              }
            } else if (Array.isArray(allCheckpointsResponse)) {
              // Response is directly an array
              pageCheckpoints = allCheckpointsResponse;
            }
          }

          allCheckpoints = [...allCheckpoints, ...pageCheckpoints];

          // Check if there are more pages
          const pagination = (allCheckpointsResponse as any)?.pagination;
          if (pagination) {
            hasMore = currentPage < pagination.totalPages;
          } else {
            // If no pagination info, assume no more pages if we got less than the limit
            hasMore = pageCheckpoints.length === CHECKPOINT_API_MAX_LIMIT;
          }

          currentPage++;
        }

        console.log('Refreshed checkpoints for asset:', allCheckpoints.length);

        // Calculate counts
        const totalCheckpoints = allCheckpoints.length;
        const activeCheckpoints = allCheckpoints.filter((cp: any) => cp.status === 'active').length;

        // Always show all checkpoints (active + inactive) in the drawer so users
        // can enable/disable them from here.
        setSelectedAssetData({
          ...selectedAssetData,
          checkpoints: allCheckpoints,
          totalCheckpoints,
          activeCheckpoints
        });
      } catch (error) {
        console.error('Failed to refresh asset checkpoint data:', error);
        toast.error('Failed to refresh checkpoint data');
      }
    }
  };

  const handleStatusFilterChange = async (newStatus: string) => {
    setStatusFilter(newStatus);
    if (viewMode === 'assets') {
      if (checkpointType === 'OMS') {
        await fetchProjectTypeStats();
        await fetchAssetsWithCheckpoints(1, false);
      } else if (checkpointType === 'AMS') {
        await fetchAmsProjectTypeStats();
      }
    }
  };

  const handleLoadMoreAssets = async () => {
    const nextPage = assetsPage + 1;
    await fetchAssetsWithCheckpoints(nextPage, true);
  };

  // Handle add checkpoint for specific asset
  const handleAddCheckpoint = async (assetData?: AssetWithCheckpoints, checkpointData?: { name: string; frequency: string; bufferTimeDays?: number; requirement?: string }) => {
    if (assetData && checkpointData) {
      try {
        // Create the new checkpoint via API
        const newCheckpoint = {
          name: checkpointData.name,
          frequency: checkpointData.frequency as "Weekly" | "Daily" | "Monthly" | "Quarterly" | "Yearly" | "Half-Yearly",
          bufferTimeDays: checkpointData.bufferTimeDays, // Include grace period
          requirement: checkpointData.requirement, // Optional requirement field
          assetId: assetData.asset.id || assetData.asset._id,
          projectTypeId: typeof assetData.asset.projectTypeId === 'string'
            ? assetData.asset.projectTypeId
            : assetData.asset.projectTypeId._id,
          status: 'active' as 'active' | 'inactive'
        };

        await checkpointService.createCheckpoint(newCheckpoint);

        // Show success message
        toast.success(`Checkpoint "${checkpointData.name}" added successfully!`);

        // Refresh the assets data to show the new checkpoint
        await fetchAssetsWithCheckpoints(1, false);

      } catch (error) {
        console.error('Failed to create checkpoint:', error);
        toast.error('Failed to create checkpoint. Please try again.');
      }
    } else if (assetData) {
      // Pre-populate the form with asset and project type data (for the old flow)
      // setAsset(assetData.asset.id || assetData.asset._id); // setAsset is unused
      if (assetData.asset.projectTypeId && typeof assetData.asset.projectTypeId === 'object') {
        setProjectType(assetData.asset.projectTypeId._id);
      }
      onAdd(assetData);
    }
  };

  // AMS Event Handlers
  const handleAmsAssetView = (asset: Asset) => {
    setSelectedAmsAsset(asset);
    setSelectedAmsProject(null);
    setShowAmsDrawer(true);
  };

  const handleAmsProjectView = (projectType: { _id: string; id?: string; name: string; code?: string }) => {
    setSelectedAmsProject(projectType);
    setSelectedAmsAsset(null);
    setShowAmsDrawer(true);
  };

  // ========== Render ==========

  return (
    <div className="space-y-6">
      {/* View Mode Toggle */}
      {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-gray-900">Checkpoints</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors duration-200 ${
                  viewMode === 'table'
                    ? 'bg-green-100 text-green-700 border border-green-300'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                Table View
              </button>
              <button
                onClick={() => setViewMode('assets')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors duration-200 ${
                  viewMode === 'assets'
                    ? 'bg-green-100 text-green-700 border border-green-300'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                Assets View
              </button>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={onRefresh}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
              title="Refresh data"
            >
              <RefreshCw size={16} />
            </button>
            <button
              onClick={onAdd}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200"
            >
              <Plus size={16} />
              Add Checkpoint
            </button>
            <button
              onClick={onBulkAdd}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
            >
              <ListChecks size={16} />
              Bulk Add
            </button>
          </div>
        </div>
      </div> */}

      {/* Department-based content - automatically determined from global activeDepartment */}

      {/* Project Type Selector (Assets View) - Show for both OMS and AMS */}
      {viewMode === 'assets' && (checkpointType === 'OMS' || checkpointType === 'AMS') && (
        <ProjectTypeSelector
          projectTypeStats={projectTypeStats}
          selectedProjectType={projectType}
          onProjectTypeChange={handleProjectTypeChange}
          statusFilter={statusFilter}
          checkpointType={checkpointType}
          amsProjectCheckpoints={checkpointType === 'AMS' ? amsProjectCheckpoints : undefined}
          onAmsProjectView={checkpointType === 'AMS' ? handleAmsProjectView : undefined}
        />
      )}


      {/* OMS Content - Show current UI */}
      {checkpointType === 'OMS' && (
        <AssetsGrid
          assetsWithCheckpoints={assetsWithCheckpoints}
          search={search}
          setSearch={setSearch}
          onAssetCheckpointsClick={handleAssetCheckpointsClick}
          onAdd={handleAddCheckpoint}
          onBulkAdd={onBulkAdd}
          statusFilter={statusFilter}
          onStatusFilterChange={handleStatusFilterChange}
          isLoading={isLoadingStats}
          currentPage={assetsPage}
          hasMore={hasMoreAssets}
          onLoadMore={handleLoadMoreAssets}
        />
      )}

      {/* AMS Content */}
      {checkpointType === 'AMS' && (
        <AmsCheckpointGrid
          key={`ams-${projectType || 'no-selection'}`} // Force re-render when projectType changes
          search={search}
          setSearch={setSearch}
          checkpointTypeFilter={amsCheckpointTypeFilter}
          setCheckpointTypeFilter={setAmsCheckpointTypeFilter}
          onBulkAdd={onBulkAdd}
          onAdd={() => {}} // Not used, form is handled internally
          onAssetView={handleAmsAssetView}
          onProjectView={handleAmsProjectView}
          projectTypeId={projectType}
          projectTypeName={projectTypeStats.find((s) => (s.id || s._id) === projectType)?.name}
        />
      )}


      {/* Asset Checkpoints Drawer */}
      <AssetCheckpointsDrawer
        assetData={selectedAssetData}
        open={showAssetCheckpointsDrawer}
        onClose={handleCloseAssetCheckpointsDrawer}
        onAdd={onAdd}
        onRefresh={handleRefreshAssetData}
      />

      {/* AMS Checkpoint Drawer */}
      <AmsCheckpointDrawer
        open={showAmsDrawer}
        onClose={() => {
          setShowAmsDrawer(false);
          setSelectedAmsAsset(null);
          setSelectedAmsProject(null);
        }}
        type={selectedAmsAsset ? 'asset' : 'project'}
        asset={selectedAmsAsset || undefined}
        projectType={selectedAmsProject || undefined}
        projectTypeId={selectedAmsProject ? (selectedAmsProject._id || selectedAmsProject.id) : projectType}
      />
    </div>
  );
};

export default CheckpointTable; 